import type { PropsWithChildren, ReactNode } from 'react';
import React, { Suspense } from 'react';

import { Navigate } from 'react-router';

import { pipe } from '@code-expert/prelude';
import type { CourseOption } from '/imports/api/rpc/courseSelect/shared';
import type { CourseUrl, SemesterId } from '/imports/domain';
import { concatRoutes, formatRoute, parser } from '/imports/modules/Routing';
import { Authorise } from '/imports/ui/components/Authorise';
import { Loading } from '/imports/ui/components/Loading';
import { setPageParamsUiState } from '/imports/ui/hooks/usePageParamsOrUiState';
import { useSetPreferredEntry } from '/imports/ui/hooks/useSetPreferredEntry';
import { TopNavLayout } from '/imports/ui/layouts/TopNavLayout';
import { setPageParamsAdt } from '/imports/ui/navigation/pageParams';
import type { Page } from './pages';
import {
  analyticsRoute,
  appointmentsRoute,
  backupRoute,
  codeExamplesRoute,
  courseRoute,
  exercisesRoute,
  groupsRoute,
  listInvitationsRoute,
  ltiGradingRoute,
  messagesLink,
  messagesRoute,
  myCoursesRoute,
  regradeRoute,
  regradeTaskRoute,
  settingsRoute,
  studentsRoute,
} from './routes';

const Analytics = React.lazy(() => import('/imports/ui/pages/myCourses/analytics/Analytics'));
const Appointments = React.lazy(() => import('/imports/ui/pages/myCourses/appointments'));
const Backup = React.lazy(() => import('/imports/ui/pages/myCourses/backup/CourseBackup'));
const CodeExamples = React.lazy(() => import('/imports/ui/pages/myCourses/exercises/CodeExamples'));
const Entry = React.lazy(() => import('/imports/ui/pages/myCourses/Entry'));
const Exercises = React.lazy(() => import('/imports/ui/pages/myCourses/exercises/Exercises'));
const Groups = React.lazy(() => import('/imports/ui/pages/myCourses/groups/Groups'));
const Invitations = React.lazy(
  () => import('/imports/modules/Invitations/presentation/Invitations'),
);
const LtiGrading = React.lazy(() => import('/imports/ui/pages/myCourses/ltiGrading/LtiGrading'));
const Messages = React.lazy(() => import('/imports/ui/pages/myCourses/messages/Messages'));
const Navigation = React.lazy(() => import('/imports/ui/pages/myCourses/Navigation'));

const Regrade = React.lazy(() =>
  import('/imports/ui/pages/myCourses/regrade/Regrade').then(({ RegradeCourse }) => ({
    default: RegradeCourse,
  })),
);

const Settings = React.lazy(() => import('/imports/ui/pages/myCourses/settings'));
const Students = React.lazy(
  () => import('/imports/ui/pages/myCourses/studentOverviewTable/StudentOverviewAdmin'),
);

const MyCoursesWrapper = ({ children }: PropsWithChildren) => (
  <Authorise
    allow={['courseAdmin']}
    deny={['lti']}
    layout={(children) => <TopNavLayout selected="mycourses">{children}</TopNavLayout>}
  >
    <Suspense fallback={<Loading />}>{children}</Suspense>
  </Authorise>
);

const MyCoursesPage = ({
  page,
  semester,
  courseUrl,
  children,
}: {
  page: Page;
  semester: SemesterId;
  courseUrl: CourseUrl;
  children: (_: CourseOption) => ReactNode;
}) => {
  useSetPreferredEntry({ path: formatRoute(myCoursesRoute)({}), courseUrl, semester });
  setPageParamsUiState(setPageParamsAdt.mycourses({ semester, courseUrl, page }));

  return (
    <MyCoursesWrapper>
      <Navigation page={page} semester={semester} courseUrl={courseUrl}>
        {children}
      </Navigation>
    </MyCoursesWrapper>
  );
};

export const myCoursesRouter = concatRoutes([
  pipe(
    analyticsRoute.parser,
    parser.map(({ semester, courseUrl, section, highscoreChartSection }) => (
      <MyCoursesPage page="analytics" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => (
          <Analytics
            semester={semester}
            courseId={_id}
            section={section}
            highscoreChartSection={highscoreChartSection}
          />
        )}
      </MyCoursesPage>
    )),
  ),
  pipe(
    appointmentsRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="appointments" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => <Appointments semester={semester} courseId={_id} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    backupRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="backup" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => <Backup semester={semester} courseId={_id} courseUrl={courseUrl} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    codeExamplesRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="codeExamples" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => <CodeExamples semester={semester} courseId={_id} courseUrl={courseUrl} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    exercisesRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="exercises" semester={semester} courseUrl={courseUrl}>
        {() => <Exercises semester={semester} courseUrl={courseUrl} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    groupsRoute.parser,
    parser.map(({ semester, courseUrl, section }) => (
      <MyCoursesPage page="groups" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => (
          <Groups semester={semester} courseId={_id} courseUrl={courseUrl} section={section} />
        )}
      </MyCoursesPage>
    )),
  ),
  pipe(
    ltiGradingRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="ltiGrading" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => <LtiGrading semester={semester} courseId={_id} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    messagesRoute.parser,
    parser.map(({ semester, courseUrl, section }) => (
      <MyCoursesPage page="messages" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => (
          <Messages
            semester={semester}
            courseId={_id}
            section={section}
            sectionLink={(section) => messagesLink({ semester, courseUrl, section })}
          />
        )}
      </MyCoursesPage>
    )),
  ),
  pipe(
    regradeRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="regrade" courseUrl={courseUrl} semester={semester}>
        {(course) => <Regrade course={course} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    regradeTaskRoute.parser,
    parser.map(({ semester, courseUrl, taskId }) => (
      <MyCoursesPage page={'regrade'} semester={semester} courseUrl={courseUrl}>
        {(course) => <Regrade course={course} taskId={taskId} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    settingsRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="settings" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => <Settings semester={semester} courseId={_id} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    studentsRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesPage page="students" semester={semester} courseUrl={courseUrl}>
        {({ _id }) => <Students semester={semester} courseId={_id} courseUrl={courseUrl} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    listInvitationsRoute.parser,
    parser.map(({ semester, courseUrl, section }) => (
      <MyCoursesPage page="invitations" semester={semester} courseUrl={courseUrl}>
        {() => <Invitations semester={semester} courseUrl={courseUrl} section={section} />}
      </MyCoursesPage>
    )),
  ),
  pipe(
    courseRoute.parser,
    parser.map(({ semester, courseUrl }) => (
      <MyCoursesWrapper>
        <Navigate to={formatRoute(exercisesRoute)({ semester, courseUrl })} />
      </MyCoursesWrapper>
    )),
  ),
  pipe(
    myCoursesRoute.parser,
    parser.map(() => (
      <MyCoursesWrapper>
        <Suspense fallback={<Loading />}>
          <Entry />
        </Suspense>
      </MyCoursesWrapper>
    )),
  ),
]);
